import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"
import { useCallback } from "react"
import {
  FEATURE_ANNOUNCEMENTS_ENDPOINTS,
  FeatureAnnouncementCategory,
  IFeatureAnnouncement,
  featureAnnouncementsService,
} from "services/featureAnnouncementsService"
import { useAuthStore } from "stores"
import { isLocalOrDev, isPadeDomain } from "utils/utils"

/**
 * PaidHR hosts, plus local and the dev deployment (whitestone), which isPadeDomain
 * does not cover. Gates the queries and the top-bar pill alike.
 */
export const isWhatsNewEnabled = () => isPadeDomain() || isLocalOrDev()

/**
 * Unseen announcements marked for the login popup. Keyed by user so a second account
 * signing in on the same tab does not inherit the first one's answer.
 */
export const useUnseenFeatureAnnouncements = () => {
  const userName = useAuthStore((state) => state.user?.userName)

  return useQuery({
    queryKey: [FEATURE_ANNOUNCEMENTS_ENDPOINTS.SELECT_UNSEEN, userName],
    queryFn: () => featureAnnouncementsService.selectUnseen(),
    enabled: isWhatsNewEnabled() && !!userName,
    refetchOnWindowFocus: false,
    select: (res) => res?.data ?? [],
  })
}

export const useFeatureAnnouncements = (
  page: number,
  limit: number,
  category?: FeatureAnnouncementCategory
) => {
  const userName = useAuthStore((state) => state.user?.userName)

  return useQuery({
    queryKey: [
      FEATURE_ANNOUNCEMENTS_ENDPOINTS.SELECT,
      userName,
      page,
      limit,
      category,
    ],
    queryFn: () =>
      featureAnnouncementsService.select({ page, limit, category }),
    enabled: isWhatsNewEnabled() && !!userName,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  })
}

/**
 * How many unseen announcements the user has, including ones kept out of the popup, so
 * the nav dot still hints at page-only updates.
 */
export const useUnseenFeatureAnnouncementCount = () => {
  const userName = useAuthStore((state) => state.user?.userName)

  return useQuery({
    queryKey: [FEATURE_ANNOUNCEMENTS_ENDPOINTS.UNSEEN_COUNT, userName],
    queryFn: () => featureAnnouncementsService.unseenCount(),
    enabled: isWhatsNewEnabled() && !!userName,
    refetchOnWindowFocus: false,
    select: (res) => res?.data?.count ?? 0,
  })
}

/** Counts per category for the "Last 30 days" card, from the server. */
export const useFeatureAnnouncementSummary = () => {
  const userName = useAuthStore((state) => state.user?.userName)

  return useQuery({
    queryKey: [FEATURE_ANNOUNCEMENTS_ENDPOINTS.SUMMARY, userName],
    queryFn: () => featureAnnouncementsService.summary(),
    enabled: isWhatsNewEnabled() && !!userName,
    refetchOnWindowFocus: false,
    select: (res) => res?.data,
  })
}

/** Announcements already marked this visit, so repeat clicks do not call the API again. */
const markedThisVisit = new Set<number>()

/**
 * Marks one announcement seen when the user engages with it: Read more, playing its
 * video, or the popup's Learn more. Landing on a page marks nothing. Afterwards the New
 * labels, the bell count and the popup's list refresh.
 */
export const useMarkFeatureAnnouncementSeen = () => {
  const queryClient = useQueryClient()

  const { mutate } = useMutation({
    mutationFn: (id: number) => featureAnnouncementsService.markSeen(id),
    onSuccess: () => {
      queryClient.invalidateQueries([
        FEATURE_ANNOUNCEMENTS_ENDPOINTS.SELECT_UNSEEN,
      ])
      queryClient.invalidateQueries([
        FEATURE_ANNOUNCEMENTS_ENDPOINTS.UNSEEN_COUNT,
      ])
      queryClient.invalidateQueries([FEATURE_ANNOUNCEMENTS_ENDPOINTS.SELECT])
    },
    onError: (_error, id) => {
      // Let a later engagement try again.
      markedThisVisit.delete(id)
    },
  })

  return useCallback(
    (item?: Pick<IFeatureAnnouncement, "id" | "is_new"> | null) => {
      if (!item || item.is_new === false || markedThisVisit.has(item.id)) return
      markedThisVisit.add(item.id)
      mutate(item.id)
    },
    [mutate]
  )
}

/** Release dates come back as UTC without an offset; read them as UTC. */
export const parseReleaseDate = (value?: string | null) => {
  if (!value) return null
  const utc = /(z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`
  return dayjs(utc)
}

export const formatReleaseDate = (value?: string | null) =>
  parseReleaseDate(value)?.format("D MMM YYYY") ?? ""

/** Announcement bodies are rich-text HTML; summaries show them as plain text. */
export const toPlainText = (html?: string | null) => {
  if (!html) return ""
  const text = new DOMParser().parseFromString(html, "text/html").body
    .textContent
  return (text ?? "").replace(/\s+/g, " ").trim()
}

/**
 * Announcements carry no icon, so tiles cycle through these tints. Swap for a
 * per-announcement icon once the API returns one (see README).
 */
export const TILE_CLASSES = [
  "bg-primary50 text-primary500",
  "bg-[#9747FF1A] text-purple",
  "bg-[#E8F7EF] text-[#16A34A]",
  "bg-[#FFF3E6] text-[#EA580C]",
]

/** In-app paths stay in the tab; anything else opens in a new one. */
export const isInAppLink = (link?: string | null) =>
  !!link && link.startsWith("/") && !link.startsWith("//")
